import * as React from 'react';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import type {Row} from 'shells/table/types';

import type {DomainFragment} from 'nutshell-graphql-types';

import {AddSubdomainModal} from './add-subdomain-modal';
import {useDeleteSubdomain} from './graphql/hooks';
import {RecordTable} from './record-table';
import {SubdomainRowActionMenu} from './subdomain-row-action-menu';
import {TableTitle} from './table-title';

import styles from './domain-details.module.css';

type Props = {
    domain: DomainFragment;
    toggleRowExpanded: (arg1: boolean) => void;
};

export function DomainDetails(props: Props) {
    const [isAddingSubdomain, setIsAddingSubdomain] = React.useState<boolean>(false);

    const {deleteSubdomain} = useDeleteSubdomain();

    const firstRow = (
        <AddButtonRow buttonText='Add new subdomain' onClick={() => setIsAddingSubdomain(true)} />
    );

    const renderRowActionMenu = (row: Row<any>) => {
        // Extract subdomain by removing domain name from record
        const subdomain = row.original.record.replace(`.${props.domain.name}`, '');

        const onDeleteSubdomain = async () => {
            await deleteSubdomain({
                domain: props.domain.name,
                subdomain: subdomain,
            });
            props.toggleRowExpanded(true);
        };

        return <SubdomainRowActionMenu onDeleteSubdomain={onDeleteSubdomain} />;
    };

    return (
        <>
            <div className={styles['details-container']}>
                <StackLegacy spacing={20}>
                    <div>
                        <div className={styles.header}>
                            {NutClientConfig.hasDmarcSetup ? 'DKIM, SPF, & DMARC' : 'DKIM & SPF'}
                        </div>
                        <StackLegacy spacing={8}>
                            <TableTitle
                                domainName={props.domain.name}
                                title={props.domain.groupSpf.name}
                                status={props.domain.groupSpf.status}
                            />
                            <div className='mb-8'>
                                <RecordTable
                                    records={props.domain.groupSpf.records}
                                    includePriority={true}
                                />
                            </div>
                            <TableTitle
                                domainName={props.domain.name}
                                title={props.domain.groupDkim.name}
                                status={props.domain.groupDkim.status}
                            />
                            <RecordTable records={props.domain.groupDkim.records} />

                            {/*
                            Feature flag on each element so they all remain
                            in the stack and get proper spacing.
                         */}
                            {NutClientConfig.hasDmarcSetup && (
                                <TableTitle
                                    domainName={props.domain.name}
                                    title={props.domain.groupDmarc.name}
                                    status={props.domain.groupDmarc.status}
                                />
                            )}
                            {NutClientConfig.hasDmarcSetup && (
                                <RecordTable records={props.domain.groupDmarc.records} />
                            )}
                        </StackLegacy>
                    </div>
                    <div>
                        <div className={styles.header}>Landing pages & click tracking</div>
                        <StackLegacy spacing={8}>
                            <TableTitle
                                domainName={props.domain.name}
                                title={props.domain.groupCustom.name}
                                status={props.domain.groupCustom.status}
                            />
                            <RecordTable
                                records={props.domain.groupCustom.records}
                                firstRow={firstRow}
                                renderRowActionMenu={renderRowActionMenu}
                            />
                        </StackLegacy>
                    </div>
                    <div className='flex mb-8'>
                        <Link
                            variant='primary'
                            size='small'
                            href='https://support.nutshell.com/en/articles/8428873-campaigns-how-to-set-up-dkim-spf-and-dmarc'
                            newTab={true}
                        >
                            Read our instructions
                        </Link>
                        &nbsp;for how to configure these records with common hosting providers.
                    </div>
                </StackLegacy>
            </div>
            {isAddingSubdomain && (
                <AddSubdomainModal
                    domain={props.domain.name}
                    setIsAddingSubdomain={setIsAddingSubdomain}
                    toggleRowExpanded={props.toggleRowExpanded}
                />
            )}
        </>
    );
}
